import { ArrowRight, Facebook, Instagram } from "@/lib/icons";
import { Link } from "react-router-dom";
import KofiButton from "./KofiButton";
import { NAV_CATEGORIES } from "@/data/navigation";
import BrandMark from "./BrandMark";

/** Cap long category lists in the footer so columns stay roughly even. */
const FOOTER_TOOL_CAP = 5;
/** Categories that have a dedicated hub route worth linking to. */
const HUB_ROUTES: Partial<Record<string, { path: string; label: string }>> = {
  play: { path: "/play", label: "See all games" },
};

export default function SiteFooter() {
  return (
    <footer
      role="contentinfo"
      className="footer-floor border-t border-border bg-background/60 mt-auto"
    >
      <div className="max-w-[1200px] mx-auto px-4 py-8">
        {/* Grouped tool columns - mirrors header IA */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-6 mb-8">
          <div className="col-span-2 sm:col-span-3 lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-2 text-base font-semibold tracking-tight text-foreground">
              <BrandMark size={20} />
              <span>Element<span className="text-primary">Pad</span></span>
            </Link>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Interactive chemistry free for all
            </p>
            <div className="mt-3 flex flex-col items-start gap-2">
              <div className="flex items-center gap-1">
                <a
                  href="https://www.facebook.com/profile.php?id=61570660322326"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="ElementPad on Facebook"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                >
                  <Facebook size={16} aria-hidden="true" />
                </a>
                <a
                  href="https://www.instagram.com/element_pad/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="ElementPad on Instagram"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                >
                  <Instagram size={16} aria-hidden="true" />
                </a>
              </div>
              <div className="[&_img]:!h-8 [&_a]:!inline-flex">
                <KofiButton />
              </div>
            </div>
          </div>

          {NAV_CATEGORIES.map((cat) => {
            const hub = HUB_ROUTES[cat.key];
            const visibleTools = cat.tools.slice(0, FOOTER_TOOL_CAP);
            const hiddenCount = cat.tools.length - visibleTools.length;
            return (
              <nav key={cat.key} aria-labelledby={`footer-${cat.key}`}>
                <h3
                  id={`footer-${cat.key}`}
                  className="text-[11px] font-semibold uppercase tracking-wider text-foreground mb-2.5"
                >
                  {cat.label}
                </h3>
                <ul className="space-y-1.5">
                  {visibleTools.map((tool) => (
                    <li key={tool.path}>
                      <Link
                        to={tool.path}
                        className="text-xs text-muted-foreground hover:text-primary transition-colors"
                      >
                        {tool.label}
                      </Link>
                    </li>
                  ))}
                  {hub && hiddenCount > 0 && (
                    <li>
                      <Link
                        to={hub.path}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                        aria-label={`${hub.label} (${cat.tools.length} total)`}
                      >
                        {hub.label}
                        <ArrowRight size={11} aria-hidden="true" />
                      </Link>
                    </li>
                  )}
                </ul>
              </nav>
            );
          })}
        </div>

        <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} ElementPad</span>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/explore" className="hover:text-primary underline-offset-4 hover:underline">
              All tools
            </Link>
            <Link to="/about" className="hover:text-primary underline-offset-4 hover:underline">
              About
            </Link>
            <Link to="/accessibility" className="hover:text-primary underline-offset-4 hover:underline">
              Accessibility
            </Link>
            <Link to="/privacy" className="hover:text-primary underline-offset-4 hover:underline">
              Privacy & Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
